import React, { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './NotificationBell.module.scss';
import topbarStyles from '../Topbar/Topbar.module.scss';
import { notificationBellIcon } from '../../assets/hostedassets';
import NotificationPanel from './NotificationPanel';
import { useUnreadCount } from './useUnreadCount';
import { useNotifications } from './useNotifications';
import { useOutsideClick } from '../../utils/constants/hooks';
import { useIsMobile } from '../_custom/Hooks/useIsMobile';
import { INVESTOR_ROUTES } from '../../utils/constants/routesConst';
import { NotificationBellProps } from './NotificationCenter.types';
import { BADGE_MAX_COUNT, BADGE_OVERFLOW_LABEL } from './NotificationCenter.constants';
import { Mixpanel } from '../../utils/mixpanel';
import { INVESTOR_NOTIFICATION_OPENED } from '../../utils/constants/mixpanelEvents/investorEvents';

const NotificationBell: React.FC<NotificationBellProps> = ({ enabled = true }) => {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    // On mobile, the bell navigates to a dedicated full page (see NotificationsPage)
    // instead of opening this popup — panelOpen can only ever become true on desktop.
    const [panelOpen, setPanelOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const { unreadCount, refresh: refreshCount } = useUnreadCount(enabled && !panelOpen);
    const {
        notifications,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        activeTab,
        setActiveTab,
        markAllRead,
        markRead,
        markUnread,
        selectedCategories,
        setSelectedCategories,
    } = useNotifications(panelOpen, { onUnreadCountChange: refreshCount });

    const handleOutsideClose = useCallback(() => setPanelOpen(false), []);
    useOutsideClick(containerRef, handleOutsideClose);

    // Opening a thread only closes the panel — read state changes exclusively through
    // the explicit Mark read / Mark unread buttons (product ask: View Thread must not
    // auto-mark as read).
    const handleItemAction = useCallback(() => setPanelOpen(false), []);

    // Early return MUST come after every hook call — otherwise the hook count changes when
    // `enabled` flips at runtime (e.g. visibility config loads after first render), triggering
    // React's "rendered more hooks than during the previous render" invariant.
    if (!enabled) return null;

    const badgeLabel = unreadCount > BADGE_MAX_COUNT ? BADGE_OVERFLOW_LABEL : unreadCount > 0 ? String(unreadCount) : null;

    return (
        <div className={styles.Container} ref={containerRef}>
            <button
                className={styles.BellButton}
                onClick={() => {
                    if (isMobile) {
                        Mixpanel.track(INVESTOR_NOTIFICATION_OPENED, { unreadCount, source: 'mobile' });
                        navigate(`${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.NOTIFICATIONS}`);
                    } else {
                        // Bell toggles the panel — only track the transition that actually opens it.
                        if (!panelOpen) Mixpanel.track(INVESTOR_NOTIFICATION_OPENED, { unreadCount, source: 'desktop' });
                        setPanelOpen((prev) => !prev);
                    }
                }}
                aria-label={`Notifications${unreadCount > 0 ? `, ${badgeLabel} unread` : ''}`}
            >
                <img src={notificationBellIcon} alt="" />
            </button>
            {badgeLabel && <div className={`${topbarStyles.redDot} ${topbarStyles.IconBadge}`}>{badgeLabel}</div>}

            {!isMobile && panelOpen && (
                <NotificationPanel
                    notifications={notifications}
                    unreadCount={unreadCount}
                    loading={loading}
                    loadingMore={loadingMore}
                    hasMore={hasMore}
                    onLoadMore={loadMore}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    onMarkAllRead={markAllRead}
                    onClose={() => setPanelOpen(false)}
                    selectedCategories={selectedCategories}
                    onCategoriesChange={setSelectedCategories}
                    onItemAction={handleItemAction}
                    onMarkRead={markRead}
                    onMarkUnread={markUnread}
                />
            )}
        </div>
    );
};

export default NotificationBell;
